import { useState, useEffect, useCallback, useRef } from 'react';
import { useTheme } from '@/components/theme-provider';
import { generateDerivedColors } from '@/lib/color-utils';

export interface PaletteColors {
  primary: string;
  background: string;
  foreground: string;
  accent: string;
  success?: string;
  warning?: string;
  destructive?: string;
  info?: string;
}

export interface PaletteTheme {
  light: PaletteColors;
  dark: PaletteColors;
}

export interface Palette {
  id: string;
  name: string;
  description: string;
  colors: PaletteTheme;
  source: 'apple' | 'colorhunt' | 'custom';
  createdAt?: Date;
}

// Paletas predefinidas con temas claro y oscuro
export const DEFAULT_PALETTES: Palette[] = [
  {
    id: 'apple-system',
    name: 'Apple Sistema',
    description: 'Colores oficiales del sistema Apple (iOS/macOS) según HIG',
    colors: {
      dark: {
        primary: '211 100% 50%',    // systemBlue #007AFF
        background: '0 0% 0%',      // systemBackground (Negro)
        foreground: '0 0% 100%',    // label (Blanco)
        accent: '35 100% 50%',      // systemOrange #FF9500
        success: '134 74% 49%',     // systemGreen #34C759 (corregido)
        warning: '45 100% 50%',     // systemYellow #FFCC00
        destructive: '4 90% 58%',   // systemRed #FF3B30 (corregido)
        info: '199 100% 65%'        // systemTeal #5AC8FA (corregido)
      },
      light: {
        primary: '211 100% 50%',    // systemBlue #007AFF
        background: '0 0% 100%',    // systemBackground (Blanco)
        foreground: '0 0% 0%',      // label (Negro)
        accent: '35 100% 50%',      // systemOrange #FF9500
        success: '134 74% 49%',     // systemGreen #34C759 (corregido)
        warning: '45 100% 50%',     // systemYellow #FFCC00
        destructive: '4 90% 58%',   // systemRed #FF3B30 (corregido)
        info: '199 100% 65%'        // systemTeal #5AC8FA (corregido)
      }
    },
    source: 'apple'
  },
  {
    id: 'apple-classic',
    name: 'Apple Clásica',
    description: 'Paleta monocromática elegante con acentos en rojo y naranja',
    colors: {
      dark: {
        primary: '0 0% 70%',        // Gris claro
        background: '0 0% 6%',      // Negro profundo
        foreground: '0 0% 98%',     // Blanco casi puro
        accent: '25 95% 60%',       // Naranja para resaltar
        success: '0 0% 70%',        // Gris para success
        warning: '25 95% 60%',      // Naranja para warning
        destructive: '0 84% 60%',   // Rojo para errores
        info: '0 0% 70%'            // Gris para info
      },
      light: {
        primary: '0 0% 40%',        // Gris oscuro
        background: '0 0% 100%',    // Blanco puro
        foreground: '0 0% 10%',     // Negro suave
        accent: '25 95% 50%',       // Naranja para resaltar
        success: '0 0% 40%',        // Gris oscuro para success
        warning: '25 95% 50%',      // Naranja para warning
        destructive: '0 84% 50%',   // Rojo para errores
        info: '0 0% 40%'            // Gris oscuro para info
      }
    },
    source: 'apple'
  },
  {
    id: 'apple-original',
    name: 'Apple Original',
    description: 'Paleta oficial de Apple con verde característico',
    colors: {
      dark: {
        primary: '134 76% 52%',
        background: '0 0% 7%',
        foreground: '0 0% 98%',
        accent: '35 100% 50%',
        success: '134 76% 52%',
        warning: '35 100% 50%',
        destructive: '4 100% 59%',
        info: '211 100% 50%'
      },
      light: {
        primary: '134 76% 52%',
        background: '0 0% 100%',
        foreground: '0 0% 9%',
        accent: '35 100% 50%',
        success: '134 76% 52%',
        warning: '35 100% 50%',
        destructive: '4 100% 59%',
        info: '211 100% 50%'
      }
    },
    source: 'apple'
  },
  {
    id: 'golden-elegance',
    name: 'Golden Elegance',
    description: 'Elegancia dorada con toques sofisticados',
    colors: {
      dark: {
        primary: '45 84% 51%',      // #E6B31E
        background: '0 0% 20%',     // #343434
        foreground: '48 67% 97%',   // #FCFAF1
        accent: '0 0% 79%',         // #CACACA
        success: '45 84% 51%',
        warning: '25 90% 55%',
        destructive: '4 85% 55%',
        info: '220 90% 60%'
      },
      light: {
        primary: '45 84% 41%',      // Más oscuro para contraste
        background: '48 67% 97%',   // #FCFAF1
        foreground: '0 0% 20%',     // #343434
        accent: '0 0% 50%',         // Más oscuro para contraste
        success: '45 84% 41%',
        warning: '25 90% 45%',
        destructive: '4 85% 45%',
        info: '220 90% 50%'
      }
    },
    source: 'colorhunt'
  },
  {
    id: 'ocean-depths',
    name: 'Ocean Depths',
    description: 'Profundidad oceánica profesional',
    colors: {
      dark: {
        primary: '210 100% 50%',
        background: '220 25% 15%',
        foreground: '0 0% 98%',
        accent: '200 80% 60%',
        success: '150 70% 50%',
        warning: '35 90% 60%',
        destructive: '4 85% 55%',
        info: '210 100% 50%'
      },
      light: {
        primary: '210 100% 40%',
        background: '0 0% 100%',
        foreground: '220 25% 15%',
        accent: '200 80% 50%',
        success: '150 70% 40%',
        warning: '35 90% 50%',
        destructive: '4 85% 45%',
        info: '210 100% 40%'
      }
    },
    source: 'colorhunt'
  },
  {
    id: 'forest-serenity',
    name: 'Forest Serenity',
    description: 'Serenidad del bosque con verdes naturales',
    colors: {
      dark: {
        primary: '140 60% 40%',
        background: '150 20% 12%',
        foreground: '0 0% 98%',
        accent: '120 50% 70%',
        success: '140 60% 40%',
        warning: '45 85% 60%',
        destructive: '4 85% 55%',
        info: '200 80% 60%'
      },
      light: {
        primary: '140 60% 30%',
        background: '0 0% 100%',
        foreground: '150 20% 12%',
        accent: '120 50% 60%',
        success: '140 60% 30%',
        warning: '45 85% 50%',
        destructive: '4 85% 45%',
        info: '200 80% 50%'
      }
    },
    source: 'colorhunt'
  }
];

export function usePalette() {
  const { theme } = useTheme();
  const [currentPalette, setCurrentPalette] = useState<Palette>(DEFAULT_PALETTES[0]);
  const [customPalettes, setCustomPalettes] = useState<Palette[]>([]);
  const [isApplying, setIsApplying] = useState(false);

  // Solo log una vez al inicializar
  const isInitialized = useRef(false);
  if (!isInitialized.current) {
    console.log('usePalette hook loaded - latest version');
    isInitialized.current = true;
  }

  // Aplicar paleta completa al DOM con soporte para temas claro/oscuro
  const applyPalette = useCallback(async (palette: Palette) => {
    setIsApplying(true);
    
    try {
      const root = document.documentElement;
      
      // Determinar tema actual efectivo
      const currentTheme = theme === 'system' 
        ? (window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light')
        : theme;
      
      const colors = palette.colors[currentTheme as 'light' | 'dark'];
      
      // Aplicar colores principales
      Object.entries(colors).forEach(([key, value]) => {
        if (value) {
          root.style.setProperty(`--${key}`, value);
        }
      });
      
      // Generar colores derivados basados en el sistema Apple
      const derivedColors = {
        // secondarySystemBackground y tertiarySystemBackground
        card: currentTheme === 'dark' ? '0 0% 11%' : '0 0% 95%',
        muted: currentTheme === 'dark' ? '0 0% 18%' : '0 0% 90%',
        // secondaryLabel y tertiaryLabel  
        mutedForeground: currentTheme === 'dark' ? '0 0% 70%' : '0 0% 43%',
        // separator
        border: currentTheme === 'dark' ? '0 0% 27%' : '0 0% 78%'
      };
      
      // Aplicar colores derivados
      root.style.setProperty('--card', derivedColors.card);
      root.style.setProperty('--muted', derivedColors.muted);
      root.style.setProperty('--muted-foreground', derivedColors.mutedForeground);
      root.style.setProperty('--border', derivedColors.border);
      root.style.setProperty('--input', derivedColors.border);
      
      // Colores relacionados
      root.style.setProperty('--card-foreground', colors.foreground);
      root.style.setProperty('--popover', derivedColors.card);
      root.style.setProperty('--popover-foreground', colors.foreground);
      root.style.setProperty('--secondary', derivedColors.muted);
      root.style.setProperty('--secondary-foreground', colors.foreground);
      root.style.setProperty('--accent-foreground', colors.foreground);
      root.style.setProperty('--primary-foreground', colors.foreground);
      
      // Sidebar colors (para el menú lateral)
      root.style.setProperty('--sidebar-background', colors.background);
      root.style.setProperty('--sidebar-foreground', colors.foreground);
      root.style.setProperty('--sidebar-primary', colors.primary);
      root.style.setProperty('--sidebar-primary-foreground', colors.foreground);
      root.style.setProperty('--sidebar-accent', derivedColors.muted);
      root.style.setProperty('--sidebar-accent-foreground', colors.foreground);
      root.style.setProperty('--sidebar-border', derivedColors.border);
      root.style.setProperty('--sidebar-ring', colors.primary);
      
      // Colores de estados
      root.style.setProperty('--success', colors.success || colors.primary);
      root.style.setProperty('--warning', colors.warning || colors.accent);
      root.style.setProperty('--destructive', colors.destructive || '4 100% 59%');
      root.style.setProperty('--info', colors.info || colors.primary);
      
      // Focus ring
      root.style.setProperty('--ring', colors.primary);
      
      // Optimizaciones para Retina y alta densidad
      if (window.devicePixelRatio > 1) {
        root.style.setProperty('--border-width', '0.5px');
        root.style.setProperty('--shadow-blur', '0.5px');
      }
      
      // Guardar en localStorage
      localStorage.setItem('user-palette', JSON.stringify(palette));
      setCurrentPalette(palette);
      
      return true;
    } catch (error) {
      console.error('Error applying palette:', error);
      return false;
    } finally {
      setIsApplying(false);
    }
  }, [theme]);

  // Cargar paleta guardada
  const loadSavedPalette = useCallback(() => {
    try {
      const saved = localStorage.getItem('user-palette');
      if (saved) {
        const palette = JSON.parse(saved);
        
        // Verificar si la paleta tiene el formato nuevo (con temas)
        if (palette.colors && palette.colors.dark && palette.colors.light) {
          setCurrentPalette(palette);
          applyPalette(palette);
        } else {
          // Si es formato antiguo, limpiar y usar paleta por defecto
          console.log('Formato de paleta obsoleto, usando paleta por defecto');
          localStorage.removeItem('user-palette');
          applyPalette(DEFAULT_PALETTES[0]);
        }
      } else {
        // Si no hay paleta guardada, aplicar la por defecto
        applyPalette(DEFAULT_PALETTES[0]);
      }
    } catch (error) {
      console.error('Error loading saved palette:', error);
      // En caso de error, aplicar paleta por defecto
      applyPalette(DEFAULT_PALETTES[0]);
    }
  }, [applyPalette]);

  // Restablecer a paleta por defecto
  const resetToDefault = useCallback(() => {
    applyPalette(DEFAULT_PALETTES[0]);
  }, [applyPalette]);

  // Obtener todas las paletas disponibles
  const getAllPalettes = useCallback(() => {
    return [...DEFAULT_PALETTES, ...customPalettes];
  }, [customPalettes]);

  // Forzar actualización de paleta (función de depuración)
  const forceUpdatePalette = useCallback(() => {
    // Limpiar localStorage y recargar
    localStorage.removeItem('user-palette');
    applyPalette(DEFAULT_PALETTES[0]);
    console.log('Paleta forzada a valores por defecto');
  }, [applyPalette]);

  // Crear paleta personalizada
  const createCustomPalette = useCallback((name: string, description: string, colors: PaletteTheme): Palette => {
    const newPalette: Palette = {
      id: `custom-${Date.now()}`,
      name,
      description,
      colors,
      source: 'custom',
      createdAt: new Date()
    };
    
    setCustomPalettes(prev => [...prev, newPalette]);
    return newPalette;
  }, []);

  // Re-aplicar paleta cuando cambie el tema
  useEffect(() => {
    if (currentPalette) {
      applyPalette(currentPalette);
    }
  }, [theme, applyPalette, currentPalette]);

  // Cargar paleta al inicializar
  useEffect(() => {
    loadSavedPalette();
  }, [loadSavedPalette]);

  // Escuchar cambios del sistema
  useEffect(() => {
    if (theme === 'system') {
      const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
      const handleChange = () => {
        if (currentPalette) {
          applyPalette(currentPalette);
        }
      };
      
      mediaQuery.addEventListener('change', handleChange);
      return () => mediaQuery.removeEventListener('change', handleChange);
    }
  }, [theme, currentPalette, applyPalette]);

  return {
    currentPalette,
    customPalettes,
    isApplying,
    applyPalette,
    resetToDefault,
    getAllPalettes,
    createCustomPalette,
    loadSavedPalette,
    forceUpdatePalette
  };
}